import React, { useState } from 'react';
import { useData } from '../../data/DataProvider';
import { useToast } from '../../shared/components/ToastProvider';
import { activePb } from '../../lib/pb';

export default function EngineerLoginManagement() {
  const { data } = useData();
  const { notify } = useToast();
  const [selectedEngineer, setSelectedEngineer] = useState(null);
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [showingPassword, setShowingPassword] = useState(false);

  // Get engineers with accounts
  const engineers = data.engineers || [];

  const handleViewCredentials = async (engineer) => {
    try {
      // Find the account linked to this engineer
      const pb = activePb();
      const accounts = await pb.collection('accounts').getFullList({
        filter: `role = "engineer" && name = "${engineer.name}" && tenant_id = "${data.tenant.id}"`,
        limit: 1
      });
      
      if (accounts.length > 0) {
        const account = accounts[0];
        setSelectedEngineer({
          ...engineer,
          account_id: account.id,
          email: account.email,
          status: account.status,
          last_login: account.last_login
        });
        setPassword('');
        setShowingPassword(false);
      } else {
        notify('No account found for this engineer. Click "Create Account" to set up login access.', 'error');
        setSelectedEngineer({
          ...engineer,
          account_id: null,
          email: '',
          status: 'active'
        });
      }
    } catch (err) {
      notify(err.message || 'Failed to load engineer account', 'error');
    }
  };

  const handleResetPassword = async () => {
    if (!selectedEngineer || !password) {
      notify('Password is required', 'error');
      return;
    }
    
    setBusy(true);
    try {
      const pb = activePb();
      await pb.collection('accounts').update(selectedEngineer.account_id, {
        password: password,
        passwordConfirm: password
      });
      notify('Password reset successfully');
      setPassword('');
      setShowingPassword(false);
    } catch (err) {
      notify(err.message || 'Failed to reset password', 'error');
    } finally {
      setBusy(false);
    }
  };

  const handleCreateAccount = async () => {
    const email = prompt(`Enter email for ${selectedEngineer.name}:`);
    if (!email) return;
    
    const initialPassword = prompt('Enter initial password:');
    if (!initialPassword) return;
    
    setBusy(true);
    try {
      const pb = activePb();
      const account = await pb.collection('accounts').create({
        email: email,
        password: initialPassword,
        passwordConfirm: initialPassword,
        name: selectedEngineer.name,
        role: 'engineer',
        tenant_id: data.tenant.id,
        status: 'active'
      });
      
      notify('Engineer account created successfully');
      setSelectedEngineer({
        ...selectedEngineer,
        account_id: account.id,
        email: account.email,
        status: account.status,
        last_login: account.last_login
      });
      setPassword('');
      setShowingPassword(false);
    } catch (err) {
      notify(err.message || 'Failed to create engineer account', 'error');
    } finally {
      setBusy(false);
    }
  };

  const engineerLoginUrl = `${window.location.origin}/#/engineer/login/${data.tenant.slug}`;

  return (
    <section className="panel settings-form">
      <h3>Engineer Login Management</h3>
      <p className="field-hint">Manage engineer portal access and credentials</p>
      
      {engineers.length === 0 ? (
        <p className="field-hint">No engineers configured yet. Add engineers in the Clients section.</p>
      ) : (
        <>
          {!selectedEngineer ? (
            <div className="engineer-list">
              {engineers.map((engineer) => (
                <div key={engineer.id} className="engineer-item">
                  <div>
                    <strong>{engineer.name}</strong>
                    {engineer.email && <small>{engineer.email}</small>}
                  </div>
                  <button 
                    className="secondary-button" 
                    onClick={() => handleViewCredentials(engineer)}
                  >
                    Manage Access
                  </button>
                </div>
              ))}
            </div>
          ) : (
            <>
              <button 
                className="secondary-button" 
                onClick={() => setSelectedEngineer(null)}
                style={{ marginBottom: '1rem' }}
              >
                ← Back to Engineers
              </button>
              
              <div className="selected-engineer">
                <h4>{selectedEngineer.name}</h4>
                
                {selectedEngineer.account_id ? (
                  <>
                    <label>Email
                      <input 
                        type="email" 
                        value={selectedEngineer.email || ''} 
                        disabled 
                      />
                    </label>
                    
                    <label>Status
                      <input 
                        type="text" 
                        value={selectedEngineer.status || 'active'} 
                        disabled 
                      />
                    </label>
                    
                    <label>Account ID
                      <input 
                        type="text" 
                        value={selectedEngineer.account_id} 
                        disabled 
                        style={{ fontFamily: 'monospace', fontSize: '0.875rem' }}
                      />
                    </label>
                    
                    {selectedEngineer.last_login && (
                      <label>Last Login
                        <input 
                          type="text" 
                          value={new Date(selectedEngineer.last_login).toLocaleString()} 
                          disabled 
                        />
                      </label>
                    )}
                    
                    <div className="password-section">
                      <h4>Reset Password</h4>
                      <label>New Password
                        <div style={{ display: 'flex', gap: '0.5rem' }}>
                          <input 
                            type={showingPassword ? "text" : "password"} 
                            value={password} 
                            onChange={(e) => setPassword(e.target.value)}
                            placeholder="Enter new password"
                          />
                          <button 
                            type="button" 
                            className="secondary-button"
                            onClick={() => setShowingPassword(!showingPassword)}
                          >
                            {showingPassword ? 'Hide' : 'Show'}
                          </button>
                        </div>
                      </label>
                      
                      <button 
                        className="primary-button" 
                        onClick={handleResetPassword}
                        disabled={busy || !password}
                      >
                        {busy ? 'Resetting…' : 'Reset Password'}
                      </button>
                    </div>
                  </>
                ) : (
                  <div className="no-account-section">
                    <p className="field-hint">
                      This engineer doesn't have a login account yet.
                    </p>
                    <button 
                      className="primary-button" 
                      onClick={handleCreateAccount}
                      disabled={busy}
                    >
                      {busy ? 'Creating…' : 'Create Login Account'}
                    </button>
                  </div>
                )}
              </div>
            </>
          )}
          
          <div className="portal-links-section">
            <h4>Engineer Portal Links</h4>
            <p className="field-hint">Share this login link with your engineers</p>
            
            <label>Engineer Login URL
              <input 
                type="text" 
                value={engineerLoginUrl}
                readOnly 
                onClick={(e) => e.target.select()}
                style={{ cursor: 'pointer', fontFamily: 'monospace' }}
              />
            </label>
          </div>
        </>
      )}
    </section>
  );
}